import { SwapRightOutlined } from '@ant-design/icons';
import { ProFormSelect, ProFormGroup, ProFormField } from '@ant-design/pro-form';
import { useHover } from 'ahooks';
import { Form, Modal, Select, SelectProps, Tooltip } from 'antd';
import { FormInstance } from 'antd/lib/form/hooks/useForm';
import TextArea from 'antd/lib/input/TextArea';
import classnames from 'classnames';
import { isArray } from 'lodash-es';
import React, { FC, useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import IconClose from '@/assets/images/icons/icon-close.react.svg?react';
import  StringTooltipIcon from '@/assets/images/icons/string-tooltip-icon.react.svg?react';
import { DATA_TID } from '@/infrastructure/cypress-ids';
import { VersionSelectGroupFormType } from '@/presentation/components/form';
import { CustomAutoComplete } from '@/presentation/components/form/components/auto-complete';
import { getValueFromEvent, useOptions } from '@/presentation/components/form/utils';
import { TdsFormLabel } from '@/presentation/components/tds-form/tds-form-label';
import { AppVersionOperator, ExOption, FIELD_ALL, LaunchType, Option } from '@/proto/field';
import { MetricSubType, MetricType } from '@/proto/metric';
import { toHumpString } from '@raft/utils/src/case/camel-case';
import { versionFilterTips } from '../../issue-list/components/tips';
import styles from './version-select-group.module.scss';

type Mode = 'multiple' | 'tags' | 'single' | undefined;

interface VersionSelectGroupProps {
  form: FormInstance,
  leftName: string;
  rightName: string;
  leftOptions: Option[] | ExOption[];
  rightOptions: Option[];
  mode: Mode;
  leftInitialValue?: any;
  rightInitialValue?: any;
  leftClassName?: string;
  rightClassName?: string;
  subType?: LaunchType | MetricType | MetricSubType;
  isCompareOpen?: boolean;
  hasMaxWidth?: boolean;
}

export enum VersionSelectGroupName {
  appVersionGroup = 'appVersionGroup',
  appVersionOperator = 'appVersionOperator',
  minAppVersion = 'minAppVersion',
  maxAppVersion = 'maxAppVersion',
  wildcardAppVersion = 'wildcardAppVersion',
}
const include = [AppVersionOperator.Or, AppVersionOperator.NOT];
export const VersionSelectGroup: FC<VersionSelectGroupProps> = (props) => {
  const [mode, setMode] = useState<Mode>(props.mode);
  const { appVersionOperator, appVersionOperatorCompare } = props.form.getFieldsValue();
  const defaultOperator = props.isCompareOpen ? appVersionOperatorCompare : appVersionOperator;
  const [operator, setOperator] = useState<AppVersionOperator>(defaultOperator || props.leftInitialValue);
  const [isFocus, setIsFocus] = useState(false);
  const [isHover, setIsHover] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [searchValue, setSearchValue] = useState<string>('');

  const renameMap = {
    minAppVersion: props.isCompareOpen ? 'minAppVersionCompare' : 'minAppVersion',
    maxAppVersion: props.isCompareOpen ? 'maxAppVersionCompare' : 'maxAppVersion',
    wildcardAppVersion: props.isCompareOpen ? 'wildcardAppVersionCompare' : 'wildcardAppVersion',
  };

  const onChangeOperator = (operator: AppVersionOperator) => {
    setOperator(operator);
    props.form.resetFields([props.rightName]);
    if (include.includes(operator)) {
      setMode('tags');
      return;
    }
    setMode(undefined);
  };

  const {
    appVersionGroup,
    version,
    appVersionGroupCompare,
    versionCompare,
  } = props.form.getFieldsValue();
  const appVersion = props.isCompareOpen
    ? appVersionGroupCompare || versionCompare
    : appVersionGroup || version;
  const tips = isArray(appVersion) ? <>{appVersion?.map(item => <div>{item}</div>)}</> : '';
  const tipsVisible = isHover && !isFocus && !dropdownVisible && (appVersion as string[])?.length > 1 && mode === 'tags';

  const rightOptions = include.includes(operator)
    ? props.rightOptions
    : props.rightOptions.filter(item => item.value !== FIELD_ALL);

  const isTags = include.includes(operator);
  const isRange = operator === AppVersionOperator.Between;
  const isInput = operator === AppVersionOperator.Wildcard;
  const isSingle = !isTags && !isRange && !isInput;

  useEffect(() => {
    if (props.subType) {
      setOperator(props.leftInitialValue);
    }
  }, [props.subType]);

  useEffect(() => {
    const { [props.leftName]: fieldVersionOperator } = props.form.getFieldsValue();
    const mode = include.includes(fieldVersionOperator)  ? 'tags' : undefined;
    setMode(mode);
    setOperator(fieldVersionOperator);
  }, [props.isCompareOpen, props.form.getFieldsValue()]);

  return <div className={styles.appVersionSelectGroup}>
    <ProFormGroup>
      <ProFormSelect
        name={props.leftName}
        options={props.leftOptions?.map?.(item => ({
          ...item,
          ['data-tid']: `appVersionOperator-${item.label}`,
        }))}
        allowClear={false}
        initialValue={props.leftInitialValue}
        fieldProps={{
          dropdownClassName: 'reset-font-size',
          onChange: onChangeOperator,
        }}
      />
      {isTags ? <Tooltip title={tips} visible={tipsVisible}>
        <ProFormSelect
          name={props.rightName}
          options={rightOptions?.map?.(item => ({
            ...item,
            ['data-tid']: `appVersion-${item.label}`,
          }))}
          className={props.rightClassName}
          initialValue={props.rightInitialValue}
          mode={mode}
          getValueFromEvent={mode ? (args: string[]) => getValueFromEvent(args, props.mode!) : undefined}
          fieldProps={{
            showSearch: true,
            maxTagCount: 'responsive',
            dropdownClassName: 'reset-font-size',
            searchValue,
            onSearch: value => setSearchValue(value),
            onSelect: () => setSearchValue(''),
            onClear: () => setSearchValue(''),
            onFocus: () => setIsFocus(true),
            onBlur: () => setIsFocus(false),
            onMouseEnter: () => setIsHover(true),
            onMouseLeave: () => setIsHover(false),
            onDropdownVisibleChange: open => setDropdownVisible(open),
          }}
        />
      </Tooltip> : null}
      {isSingle ? <ProFormField name={props.rightName} >
        <CustomAutoComplete
          name={'appVersion'}
          options={rightOptions}
          className={classnames(props.rightClassName, styles.single, 'query-form-auto-complte')}
          data-tid={`${DATA_TID.versionSelect.singleAppVersion}`}
        />
      </ProFormField> : null}
      {isRange && <div className={styles.range}>
        <ProFormField name={renameMap.minAppVersion}>
          <CustomAutoComplete
            name={'appVersion'}
            options={rightOptions}
            className={classnames(styles.rangeLeft, 'query-form-auto-complte')}
            data-tid={`${DATA_TID.versionSelect.minAppVersion}`}
          />
        </ProFormField>
        <SwapRightOutlined className={styles.outlined}/>
        <ProFormField name={renameMap.maxAppVersion}>
          <CustomAutoComplete
            name={'appVersion'}
            options={rightOptions}
            className={classnames(styles.rangeRight, 'query-form-auto-complte')}
            dropdownMatchSelectWidth={250}
            data-tid={`${DATA_TID.versionSelect.maxAppVersion}`}
          />
        </ProFormField>
      </div>}
      {isInput && <ProFormField name={renameMap.wildcardAppVersion}>
        <CustomAutoComplete
          name={'appVersion'}
          options={rightOptions}
          className={classnames(props.rightClassName, styles.wildcard, 'query-form-auto-complte')}
          data-tid={`${DATA_TID.versionSelect.wildcardAppVersion}`}
        />
      </ProFormField>}
    </ProFormGroup>
  </div>;
};

export const VersionSelectGroupForm: FC<{
  entry: VersionSelectGroupFormType,
  form: FormInstance,
  hidden?: boolean,
}> = (props) => {
  const { entry, form, hidden } = props;
  const {
    label,
    leftInitialValue,
    rightInitialValue,
    leftName,
    name,
    scope,
    mode,
    subType,
    isCompareOpen,
    hasMaxWidth,
    isSplit,
    labelTools,
  } = entry;
  const isAnalytics = scope === 'analytics';
  const leftFieldKey = 'leftFieldKey' in entry ? (entry as VersionSelectGroupFormType).leftFieldKey : undefined;
  const rightFieldKey = 'rightFieldKey' in entry ? (entry as VersionSelectGroupFormType).rightFieldKey : undefined;
  const fieldScope = 'fieldScope' in entry ? (entry as VersionSelectGroupFormType).fieldScope : undefined;
  const leftUserOptions = 'leftOptions' in entry ? (entry as VersionSelectGroupFormType).leftOptions : undefined;
  const rightUserOptions = 'rightOptions' in entry ? (entry as VersionSelectGroupFormType).rightOptions : undefined;
  const domRef = useRef(null);
  const isHovering = useHover(domRef);
  const leftOptions = useOptions({
    fieldKey: leftFieldKey,
    fieldScope,
    userOptions: leftUserOptions,
  });
  const rightOptions = useOptions({
    fieldKey: rightFieldKey,
    fieldScope,
    userOptions: rightUserOptions,
  });
  const [splitVisible, setSplitVisible] = useState(false);
  const splitRef = useRef<ISplitModalRef>();
  const leftRename = isCompareOpen ? `${toHumpString(leftName)}Compare` : toHumpString(leftName);

  const splitValue = () => {
    setSplitVisible(true);
    const {
      appVersionGroup,
    } = form.getFieldsValue();
    const values = appVersionGroup;
    if (Array.isArray(values) && values.every(str => typeof(str) === 'string')) {
      splitRef?.current?.showModal(values?.join('\n'));
    } else if (typeof(values) === 'string' || !values) {
      splitRef?.current?.showModal(values);
    }
  };

  const splitCallBack = (value?: string[]) => {
    form.setFieldsValue({
      appVersionGroup: value,
    });
    setSplitVisible(false);
  };

  return <div className={styles.tdsLabel} ref={domRef}>
  <ProFormField
    label={<TdsFormLabel isHovering={isHovering}
                         label={label}
                         tools={[
                           isSplit ? <Tooltip
                             title={<div>
                               {versionFilterTips}
                               <p><span onClick={splitValue} className={'underline cursor-pointer'}>点击icon进入批量输入编辑页</span>。</p>
                             </div>}
                             overlayStyle={{ maxWidth: 500 }}
                             placement={'top'}
                           >
                             <StringTooltipIcon onClick={splitValue} className={styles.stringTooltipIcon} />
                           </Tooltip> : undefined,
                           ...(labelTools || []),
                         ]}/>}
    hidden={hidden}>
    <VersionSelectGroup
      form={form}
      leftName={leftRename}
      leftOptions={leftOptions}
      leftInitialValue={leftInitialValue}
      rightName={name}
      rightOptions={rightOptions as Option[]}
      rightInitialValue={rightInitialValue}
      rightClassName={classnames({
        [styles.rightAnalyticsForm]: isAnalytics,
        [styles.rightIssueForm]: hasMaxWidth,
      })}
      mode={mode}
      subType={subType}
      isCompareOpen={isCompareOpen}
      hasMaxWidth={hasMaxWidth}
    />
  </ProFormField>
  <SplitModal splitVisible={splitVisible} callBack={splitCallBack}  ref={splitRef} title={typeof label === 'string' ? label :  ''}/>
  </div>;
};

enum SplitModel {
  Newline='0', // 换行
  Semicolon='1', // 分号
  CommaOrNewline='2'
}
interface ISplitModalProps{
  title: string;
  ref?: React.Ref<ISplitModalRef | undefined>;
  callBack: (value?: string[]) => void;
  splitVisible: boolean;
}

interface ISplitModalRef {
  showModal: (value?: string) => void;
}


const modelOptions: SelectProps['options'] = [
  { value: SplitModel.Newline, label: '使用回车进行分割' },
  { value: SplitModel.Semicolon, label: '使用英文分号进行分割' },
  { value: SplitModel.CommaOrNewline, label: '使用英文逗号或者回车进行分割' },
];

const SplitModal: React.FC<ISplitModalProps> = forwardRef((props, ref) => {
  const { title, callBack, splitVisible } = props;
  const [visible, setVisible] = useState<boolean>(false);
  const [form] = Form.useForm();
  useImperativeHandle(ref, () => ({
    showModal,
  }));

  const showModal = (value?: string) => {
    form.setFieldsValue({
      text: value,
    });
    setVisible(true);
  };
  const onCancel = () => {
    setVisible(false);
    form.resetFields();
  };
  const onOk = () => {
    const model = form.getFieldValue('model');
    const text = form.getFieldValue('text');
    switch (model) {
      case SplitModel.Newline:
        callBack(text?.split('\n')?.filter?.((str: string) => !!str));
        break;
      case SplitModel.Semicolon:
        callBack(text?.replaceAll(/\n/g, '')?.split(';')
          ?.filter?.((str: string) => !!str));
        break;
      case SplitModel.CommaOrNewline:
        callBack(text?.split?.(/\n|,/)?.filter?.((str: string) => !!str));
        break;
      default:
        break;
    }
    onCancel();
  };
  return <>
    {splitVisible ? <Modal
      width={903}
      maskClosable={false}
      visible={visible}
      title={title || '分割'}
      closeIcon={<IconClose/>}
      onCancel={onCancel}
      onOk={onOk}
      cancelButtonProps={{
        className: styles.cancelButton,
      }}
      okButtonProps={{
        style: { background: '#0A6CFF', borderRadius: '3px' },
      }}
      className={styles.modal}>
      <Form form={form} className={styles.form}>
        <Form.Item name={'model'} initialValue={SplitModel.Newline}>
          <Select className={'w-full'} options={modelOptions}/>
        </Form.Item>
        <Form.Item name={'text'} noStyle>
          <TextArea rows={10}/>
        </Form.Item>
      </Form>
    </Modal> : null}
  </>;
});
